// CHANGE v2 §11: nova página para listar e editar selfies anteriores.
// .pen: LNnf9/YCjxF/DWi99 (com dados), p8eEy/KxURG/c9nOA (vazio),
// LHe6S (convite atraso), a1ABf (convite futuro), Jr4CT (loading)
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Camera, Sparkle } from '@phosphor-icons/react';

import { NewSelfieButton } from '../../components/snapshots/NewSelfieButton';
import { SelfieEmptyHero } from '../../components/snapshots/SelfieEmptyHero';
import { SelfieInviteBanner } from '../../components/snapshots/SelfieInviteBanner';
import { SelfieJourneySparkline } from '../../components/snapshots/SelfieJourneySparkline';
import { SelfiePortraitList } from '../../components/snapshots/SelfiePortraitList';
import { DeleteSelfieDialog } from '../../components/snapshots/DeleteSelfieDialog';
import { WizardHelpNote } from '../../components/wizard';
import {
  useDeleteSnapshotMutation,
  useSelfieCyclesQuery,
  useSnapshotsListQuery,
  useSnapshotsTimelineQuery,
} from '../../features/snapshots/queries';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import { usePostSelfieModals } from '../../hooks/usePostSelfieModals';
import { getCurrentCycleFromList } from '../../utils/selfiePeriod';
import pt from '../../locales/pt';
import { monthLongLabel } from '../../lib/selfieDisplayUtils';

function format(template, params) {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    params[key] === undefined ? `{${key}}` : String(params[key]),
  );
}

export function SelfieHistory() {
  const location = useLocation();
  const { data: snapshots = [], isPending, isFetching } = useSnapshotsListQuery();
  const listShell = useQueryShellState({
    data: snapshots,
    isPending,
    isFetching,
    requireNonEmptyArray: false,
  });
  const { data: timeline = [], isPending: timelinePending, isFetching: timelineFetching } =
    useSnapshotsTimelineQuery();
  const timelineShell = useQueryShellState({
    data: timeline,
    isPending: timelinePending,
    isFetching: timelineFetching,
    requireNonEmptyArray: false,
  });
  const { data: cycles = [] } = useSelfieCyclesQuery();
  const currentCycle = getCurrentCycleFromList(cycles);
  const deleteMutation = useDeleteSnapshotMutation();
  const [deletingSnapshot, setDeletingSnapshot] = useState(null);

  const postSelfieSnapshot = snapshots.find(
    (row) => row.id === location.state?.postSelfieSnapshotId,
  );
  const { activateModals, modals } = usePostSelfieModals(postSelfieSnapshot);

  useEffect(() => {
    if (postSelfieSnapshot) {
      activateModals();
    }
  }, [postSelfieSnapshot, activateModals]);

  const handleConfirmDelete = async () => {
    if (!deletingSnapshot) return;
    await deleteMutation.mutateAsync(deletingSnapshot.id);
    setDeletingSnapshot(null);
  };

  // .pen: t79Te (page-head, YCjxF/DWi99 desktop only) — título+subtítulo + CTA "Nova selfie"
  const pageHeader = (
    <header className="hidden items-center justify-between gap-space-4 md:flex">
      <div className="flex flex-col gap-0.5">
        <h1 className="font-display text-type-title-page font-bold tracking-[-0.4px] text-foreground">
          {pt.snapshots.history.title}
        </h1>
        <p className="text-type-body-sm text-muted-foreground">{pt.snapshots.history.subtitle}</p>
      </div>
      <NewSelfieButton
        label={pt.patrimonio.newSelfie}
        variant="primary"
        size="sm"
        icon={Camera}
      />
    </header>
  );

  if (listShell.showBlockingSkeleton) {
    return (
      <div className="flex flex-col gap-space-5">
        {pageHeader}
        <SectionSkeleton variant="list" />
      </div>
    );
  }

  if (!snapshots.length) {
    // .pen: p8eEy/KxURG/c9nOA (Selfie · Histórico · vazio)
    return (
      <div className="flex flex-col gap-space-5">
        {pageHeader}
        <div className="flex flex-col gap-space-4">
          <SelfieEmptyHero />
          <p className="text-type-body-sm leading-[1.6] text-muted-foreground">
            {pt.snapshots.history.empty.subtitle}
          </p>
          <WizardHelpNote tone="sage" icon={Sparkle}>
            {pt.snapshots.history.empty.note}
          </WizardHelpNote>
          <NewSelfieButton
            label={pt.snapshots.history.empty.cta}
            variant="primary"
            size="md"
            icon={Camera}
            className="w-full justify-center"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-5">
      {pageHeader}
      {modals}

      <SelfieInviteBanner currentCycle={currentCycle} />

      <SelfieJourneySparkline
        timeline={timeline}
        snapshotCount={snapshots.length}
        isLoading={timelineShell.showBlockingSkeleton}
      />

      <SelfiePortraitList snapshots={snapshots} onDelete={setDeletingSnapshot} />

      <DeleteSelfieDialog
        open={Boolean(deletingSnapshot)}
        onOpenChange={(open) => {
          if (!open) setDeletingSnapshot(null);
        }}
        monthYear={
          deletingSnapshot
            ? format(pt.snapshots.history.monthYearFormat, {
                month: monthLongLabel(deletingSnapshot.reference_month),
                year: deletingSnapshot.reference_year,
              })
            : ''
        }
        onConfirm={handleConfirmDelete}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
